/**
 * 权限（RBAC）工具。
 *
 * ⚠️ 前端隐藏菜单 **不等于** 安全：后端每个 /admin/* 接口都会做二次校验
 *    （apps/backend/src/modules/admin/guards/admin-auth.guard.ts）。
 *    前端这一层只是体验层（不给用户点进去看到 403）。
 *
 * 【权限码来源】与后端 `admin.modules.permission`（AdminPermission 枚举）逐字对齐：
 *    值形如 `product:view` / `order:price` / `pay-channel:toggle`，超管用 `*` 通配。
 *    ⚠️ 后端 `isKnownPermission()` 会**拒绝写入未知权限码**，角色保存时会过滤掉，
 *    因此这里的码值一个都不能自己编。
 */
import { ErrCode } from '@weidian/contracts'
import type { AdminProfileRes } from '@weidian/contracts'

/** 权限码（与后端 AdminPermission 枚举一致） */
export const PERM = {
  /** 通配：超管拥有全部权限 */
  ALL: '*',

  // ---- 工作台 ----
  DASHBOARD_VIEW: 'dashboard:view',

  // ---- 商品与分类 ----
  PRODUCT_VIEW: 'product:view',
  PRODUCT_EDIT: 'product:edit',
  PRODUCT_DELETE: 'product:delete',
  CATEGORY_VIEW: 'category:view',
  CATEGORY_EDIT: 'category:edit',

  // ---- 营销活动（「分类」改成「活动列表」） ----
  /**
   * ⚠️ 新增权限码**必须同时出现在三处**，否则运营在角色界面**勾不到**，
   *    非超管角色永远拿不到权限（后端 @RequirePermission 一直拒 403，
   *    而功能看起来只是"打不开"）。本项目在快递100 与邀请码上各踩过一次：
   *      · apps/backend/src/modules/admin/admin.constants.ts  的 AdminPermission（真相来源）
   *      · 本文件                                            的 PERM
   *      · apps/admin/src/api/auth.ts                        的 PERMISSION_TREE_FALLBACK
   */
  ACTIVITY_VIEW: 'activity:view',
  /** 新建 / 编辑 / 上下架 / 删除活动（含关联商品） */
  ACTIVITY_EDIT: 'activity:edit',

  // ---- 订单 ----
  ORDER_VIEW: 'order:view',
  ORDER_PRICE: 'order:price',
  ORDER_ADDRESS: 'order:address',
  ORDER_REMARK: 'order:remark',
  ORDER_SHIP: 'order:ship',
  ORDER_CLOSE: 'order:close',
  ORDER_EXPORT: 'order:export',

  // ---- 售后 ----
  REFUND_VIEW: 'refund:view',
  REFUND_AUDIT: 'refund:audit',
  REFUND_EXECUTE: 'refund:execute',

  // ---- 会员与营销 ----
  MEMBER_VIEW: 'member:view',
  MEMBER_POINTS: 'member:points',
  MEMBER_EDIT: 'member:edit',
  MEMBER_MERGE: 'member:merge',
  LEVEL_VIEW: 'level:view',
  LEVEL_EDIT: 'level:edit',
  POINTS_RULE_VIEW: 'points-rule:view',
  POINTS_RULE_EDIT: 'points-rule:edit',
  COUPON_VIEW: 'coupon:view',
  COUPON_EDIT: 'coupon:edit',
  COUPON_GRANT: 'coupon:grant',

  // ---- VIP 升级码（活动商品查看权限凭证） ----
  /**
   * ⚠️ 查看与生成**分开授权**（与后端 admin.constants.ts 的注释一致）：
   *    生成升级码 = **放一个 VIP 进来**（可看全部活动商品），属敏感动作。
   *    让客服能查"这个码是谁的"不应该顺带让他能凭空造码。
   */
  INVITE_VIEW: 'invite:view',
  /** 生成 / 停用 / 删除 VIP 升级码 */
  INVITE_EDIT: 'invite:edit',

  // ---- 快递100 电子面单 ----
  /**
   * ⚠️ 取号与发货**故意分开授权**（后端 admin.constants.ts 的注释已写明）：
   *    取号会**真实消耗一张面单并产生面单费**，而客服日常只需要看单号与打印。
   *    只给「发货」权限的人不应该能重复取号烧钱。
   *
   * 这四个码原先只存在于后端 `AdminPermission` 枚举里，前端没有声明 ——
   * 后果不是"测试红"这么轻：角色界面渲染的是**声明过的码**，
   * 前端不认识的码在界面上根本勾不到，于是**超管之外的角色无法被授权使用取号功能**，
   * 而后端 @RequirePermission 会一直拒（403），功能对客服/运营等于不可用。
   */
  EXPRESS_VIEW: 'express:view',
  /** 取号并打印（消耗面单、产生费用） */
  EXPRESS_WAYBILL_CREATE: 'express:waybill-create',
  /** 作废已取号的面单（回收单号） */
  EXPRESS_WAYBILL_CANCEL: 'express:waybill-cancel',
  /** 修改快递100 设置（发货人 / 模板 / 默认内件） */
  EXPRESS_SETTING_EDIT: 'express:setting-edit',

  // ---- 支付与对账 ----
  PAYCHANNEL_VIEW: 'pay-channel:view',
  PAYCHANNEL_TOGGLE: 'pay-channel:toggle',
  PAYCHANNEL_PRIORITY: 'pay-channel:priority',
  RECONCILE_VIEW: 'reconcile:view',
  RECONCILE_RUN: 'reconcile:run',
  RECONCILE_HANDLE: 'reconcile:handle',
  RECONCILE_EXPORT: 'reconcile:export',

  // ---- 装修与内容 ----
  PAGE_VIEW: 'page:view',
  PAGE_EDIT: 'page:edit',
  BANNER_VIEW: 'banner:view',
  BANNER_EDIT: 'banner:edit',
  CONTENT_VIEW: 'content:view',
  CONTENT_EDIT: 'content:edit',
  FREIGHT_VIEW: 'freight:view',
  FREIGHT_EDIT: 'freight:edit',

  // ---- 设置与权限 ----
  SETTING_VIEW: 'setting:view',
  SETTING_EDIT: 'setting:edit',
  /** 密钥类字段的修改权限，与普通设置分开（改密钥需二次确认） */
  SETTING_SECRET: 'setting:secret',
  ADMINUSER_VIEW: 'admin-user:view',
  ADMINUSER_EDIT: 'admin-user:edit',
  ROLE_VIEW: 'role:view',
  ROLE_EDIT: 'role:edit',
  LOG_VIEW: 'log:view',
} as const

export type PermissionCode = (typeof PERM)[keyof typeof PERM]

/** 超级管理员的权限集合 */
export const SUPER_PERMISSIONS: string[] = [PERM.ALL]

/** 全部权限码（用于角色编辑页的兜底；真实清单以后端 /admin/role/permissions 为准） */
export const ALL_PERMISSION_CODES: string[] = Object.values(PERM).filter((v) => v !== PERM.ALL)

/**
 * 角色 → 默认权限矩阵。
 * 与后端 `admin-permissions.map.ts` 的 DEFAULT_ROLE_PERMISSIONS 保持一致：
 * 真实运行时权限**只以 admin_role.permissions 为准**，这里仅用于文档与「新建角色」的参考默认值。
 *
 * ============================================================================
 * ⚠️ 快递100 四个码的归属推导（2026-09 补）
 * ============================================================================
 * 推导依据是「谁已经持有语义最接近的码」，不是拍脑袋：
 *
 *  1. `express:view`（看面单记录 / 配置状态 / 打印）
 *     → 跟随 `order:ship`：全矩阵里只有 service（客服）持有发货权限。
 *       取号发货是客服的日常工作，看不到面单就没法发货。**这是有同类项可参照的推导。**
 *
 *  2. `express:waybill-create` / `express:waybill-cancel`
 *     → **默认不给任何非超管角色**。矩阵里没有同类项可参照：
 *       没有任何角色默认持有这类会「真实花钱」的操作（连运营都不默认持有改价之外的资金动作）。
 *       另外后端 admin.constants.ts 已明确「取号与发货故意分开授权」，
 *       把取号默认塞给持有发货权限的客服，正好违背这条设计。
 *       需要时由运营在角色界面按需勾选（这正是补这四个码的目的）。
 *
 *  3. `express:setting-edit`（改快递100 发货人 / 模板 / 默认内件）
 *     → 跟随 `setting:edit`。⚠️ 注意：`setting:edit` 在**本矩阵与后端
 *       DEFAULT_ROLE_PERMISSIONS 里都没有授予任何角色**（只有超管的 `*` 覆盖），
 *       所以「跟随 setting:edit」的结论就是**默认也不给**。
 *       这也和现实一致：快递100 的授权 Key 就挂在系统设置的 express 分组里，
 *       而那一页同样是「超管可改、其他人勾了才能改」。
 */
const VIEW_PERMISSIONS: string[] = [
  PERM.DASHBOARD_VIEW,
  PERM.PRODUCT_VIEW,
  PERM.CATEGORY_VIEW,
  PERM.ACTIVITY_VIEW,
  PERM.ORDER_VIEW,
  PERM.REFUND_VIEW,
  PERM.MEMBER_VIEW,
  PERM.LEVEL_VIEW,
  PERM.POINTS_RULE_VIEW,
  PERM.COUPON_VIEW,
  PERM.PAYCHANNEL_VIEW,
  PERM.RECONCILE_VIEW,
  PERM.PAGE_VIEW,
  PERM.BANNER_VIEW,
  PERM.CONTENT_VIEW,
  PERM.FREIGHT_VIEW,
  PERM.SETTING_VIEW,
]

export const ROLE_PERMISSION_TEMPLATE: Record<string, string[]> = {
  super: [PERM.ALL],
  operator: [
    ...VIEW_PERMISSIONS,
    PERM.PRODUCT_EDIT,
    PERM.PRODUCT_DELETE,
    PERM.CATEGORY_EDIT,
    /**
     * 活动：**运营的日常工作**（上/下活动、排期、挑关联商品），
     * 与后端 DEFAULT_ROLE_PERMISSIONS 的 operator 保持一致。
     * 它与 `page:edit`（首页装修）同类：都是面向 C 端展示的运营动作，不涉及资金。
     */
    PERM.ACTIVITY_EDIT,
    PERM.ORDER_REMARK,
    PERM.ORDER_EXPORT,
    PERM.ORDER_PRICE,
    PERM.MEMBER_EDIT,
    PERM.LEVEL_EDIT,
    PERM.POINTS_RULE_EDIT,
    PERM.COUPON_EDIT,
    PERM.COUPON_GRANT,
    PERM.PAYCHANNEL_TOGGLE,
    PERM.PAYCHANNEL_PRIORITY,
    PERM.PAGE_EDIT,
    PERM.BANNER_EDIT,
    PERM.CONTENT_EDIT,
    PERM.FREIGHT_EDIT,
    /**
     * VIP 升级码：**发码是运营每天的日常动作**（列表里"给谁"的备注就是给运营看的）。
     * 与后端 DEFAULT_ROLE_PERMISSIONS 的 operator 保持一致。
     *
     * ⚠️ 与 express:waybill-create 的取舍不同：取号会真实花钱且客服不需要，
     *    而升级码不花钱、且发码本身就是运营的职责范围，因此默认给运营。
     *    （运行时权限只以库中角色数据为准，这里只是"新建角色"的参考默认值。）
     */
    PERM.INVITE_VIEW,
    PERM.INVITE_EDIT,
  ],
  /** 客服：明确【没有】改价、对账、支付配置与密钥、角色权限 */
  service: [
    ...VIEW_PERMISSIONS,
    PERM.ORDER_ADDRESS,
    PERM.ORDER_REMARK,
    PERM.ORDER_SHIP,
    PERM.ORDER_CLOSE,
    PERM.REFUND_AUDIT,
    PERM.REFUND_EXECUTE,
    PERM.MEMBER_POINTS,
    PERM.MEMBER_EDIT,
    // 快递100：客服要发货就得看得见面单与打印（跟随 order:ship）。
    // ⚠️ 取号（express:waybill-create）与作废（express:waybill-cancel）**故意不给**：
    //    它们真实消耗面单并产生费用，属于需要单独授权的高风险动作，见文件头推导说明。
    PERM.EXPRESS_VIEW,
  ],
  /** 财务：对账、退款审核、导出报表 */
  finance: [
    ...VIEW_PERMISSIONS,
    PERM.RECONCILE_RUN,
    PERM.RECONCILE_HANDLE,
    PERM.RECONCILE_EXPORT,
    PERM.REFUND_AUDIT,
    PERM.REFUND_EXECUTE,
    PERM.ORDER_EXPORT,
    PERM.ORDER_VIEW,
  ],
}

/**
 * 权限判定 —— 唯一入口。
 * 支持 `*` 通配（超管）。权限为空数组时**一律不通过**（不给隐式放行）。
 */
export function hasPermission(
  permissions: readonly string[] | null | undefined,
  required?: string | string[] | undefined,
): boolean {
  if (!required) return true
  const needed = Array.isArray(required) ? required : [required]
  // 路由没要求任何权限（空数组）＝ 不需要权限，直接通过。
  // 注意区别：required 为空数组时通过，但**权限列表**为空时后面一律不通过。
  if (needed.length === 0) return true
  const list = permissions ?? []
  if (list.length === 0) return false
  if (list.includes(PERM.ALL)) return true
  return needed.every((p) => list.includes(p))
}

/** 只要命中任意一个权限即可（用于菜单聚合） */
export function hasAnyPermission(
  permissions: readonly string[] | null | undefined,
  required?: string | string[] | undefined,
): boolean {
  if (!required) return true
  const needed = Array.isArray(required) ? required : [required]
  if (needed.length === 0) return true
  const list = permissions ?? []
  if (list.length === 0) return false
  if (list.includes(PERM.ALL)) return true
  return needed.some((p) => list.includes(p))
}

/**
 * 提取本次请求真正要带上的权限要求。
 * 支持两种来源：`meta.permission`（单个/数组）与 `meta.permissions`（数组）。
 */
export function requiredPermissions(meta: Record<string, unknown> | undefined): string[] {
  if (!meta) return []
  const single = meta['permission']
  const many = meta['permissions']
  const out: string[] = []
  if (typeof single === 'string') out.push(single)
  else if (Array.isArray(single)) out.push(...single.filter((v): v is string => typeof v === 'string'))
  if (Array.isArray(many)) out.push(...many.filter((v): v is string => typeof v === 'string'))
  return out
}

/** 是否超管（roleCode=super 或持有通配权限） */
export function isSuperAdmin(
  profile: Pick<AdminProfileRes, 'roleCode' | 'permissions'> | null | undefined,
): boolean {
  if (!profile) return false
  return profile.roleCode === 'super' || profile.permissions.includes(PERM.ALL)
}

/** 权限不足的判定（与 request 层保持一致的错误码语义） */
export function isForbiddenError(err: { code?: number } | null | undefined): boolean {
  return err?.code === ErrCode.FORBIDDEN
}
